import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import getCoordinates from './geoUtils';
import api from './services/api';
import axiosInstance from './services/axiosInstance';
import { COMUNI, EMAIL_REGEX, PARTITA_IVA_REGEX } from './constants';

const CreaUtente = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    reset,
  } = useForm();

  const [categorie, setCategorie] = useState([]);
  const [coordinates, setCoordinates] = useState({ lat: '', lng: '' });

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const response = await api.getAziendaCategorie();
        if (!controller.signal.aborted) setCategorie(response.data);
      } catch (err) {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
          toast.error('Errore durante il recupero delle categorie');
        }
      }
    })();
    return () => controller.abort();
  }, []);

  const onSubmit = async (data) => {
    try {
      await axiosInstance.post('/azienda', {
        ...data,
        latitudine: coordinates.lat,
        longitudine: coordinates.lng,
      });
      toast.success('Utente creato con successo!');
      reset();
      setCoordinates({ lat: '', lng: '' });
    } catch {
      toast.error("Errore durante la creazione dell'utente.");
    }
  };

  const updateCoordinates = async (indirizzo, comune) => {
    if (!comune || !indirizzo) {
      setCoordinates({ lat: '', lng: '' });
      return;
    }
    const coords = await getCoordinates(`${indirizzo}, ${comune}`);
    setCoordinates(coords || { lat: '', lng: '' });
  };

  const handleAddressChange = (event) => {
    updateCoordinates(event.target.value, event.target.form.comune.value);
  };

  const handleCityChange = (event) => {
    const comune = event.target.value;
    updateCoordinates(event.target.form.indirizzo.value, comune);
    setValue('comune', comune);
  };

  const renderError = (field, message) =>
    errors[field] && <span className="error-message">{message}</span>;

  return (
    <div className="main-content">
      <form className="scheda-form" onSubmit={handleSubmit(onSubmit)}>
        <h2 className="scheda-form__titolo">Crea utente</h2>
        <div>
          <label htmlFor="nome">Nome titolare*</label>
          <input
            id="nome"
            type="text"
            {...register('nome', { required: 'Campo obbligatorio' })}
          />
          {renderError('nome', errors.nome?.message)}
        </div>

        <div>
          <label htmlFor="cognome">Cognome titolare*</label>
          <input
            id="cognome"
            type="text"
            {...register('cognome', { required: 'Campo obbligatorio' })}
          />
          {renderError('cognome', errors.cognome?.message)}
        </div>

        <div>
          <label htmlFor="nomeAzienda">Denominazione azienda*</label>
          <input
            id="nomeAzienda"
            type="text"
            {...register('nomeAzienda', { required: 'Campo obbligatorio' })}
          />
          {renderError('nomeAzienda', errors.nomeAzienda?.message)}
        </div>

        <div>
          <label htmlFor="indirizzo">Indirizzo*</label>
          <input
            id="indirizzo"
            type="text"
            {...register('indirizzo', { required: 'Campo obbligatorio' })}
            onBlur={handleAddressChange}
          />
          {renderError('indirizzo', errors.indirizzo?.message)}
        </div>

        <div>
          <label htmlFor="comune">Comune*</label>
          <select
            id="comune"
            {...register('comune', { required: 'Campo obbligatorio' })}
            onChange={handleCityChange}
          >
            <option value="">Seleziona un comune</option>
            {COMUNI.map((c) => (
              <option key={c.nome} value={c.nome}>
                {c.nome}
              </option>
            ))}
          </select>
          {renderError('comune', errors.comune?.message)}
        </div>

        <div>
          <label htmlFor="latitudine">Latitudine*</label>
          <input
            id="latitudine"
            type="text"
            {...register('latitudine')}
            value={coordinates.lat}
            readOnly
          />
        </div>

        <div>
          <label htmlFor="longitudine">Longitudine*</label>
          <input
            id="longitudine"
            type="text"
            {...register('longitudine')}
            value={coordinates.lng}
            readOnly
          />
        </div>

        <div>
          <label htmlFor="email">Email*</label>
          <input
            id="email"
            type="email"
            {...register('email', {
              required: 'Email obbligatoria',
              pattern: { value: EMAIL_REGEX, message: 'Formato email non valido' },
            })}
          />
          {renderError('email', errors.email?.message)}
        </div>

        <div>
          <label htmlFor="partitaIva">Partita IVA*</label>
          <input
            id="partitaIva"
            type="text"
            {...register('partitaIva', {
              required: 'Partita IVA obbligatoria',
              pattern: {
                value: PARTITA_IVA_REGEX,
                message: 'Deve contenere 11 cifre',
              },
            })}
          />
          {renderError('partitaIva', errors.partitaIva?.message)}
        </div>

        <div>
          <label htmlFor="telefono">Telefono</label>
          <input
            id="telefono"
            type="tel"
            {...register('telefono')}
          />
        </div>

        <div>
          <label htmlFor="ragioneSociale">Ragione Sociale*</label>
          <input
            id="ragioneSociale"
            type="text"
            {...register('ragioneSociale', { required: 'Campo obbligatorio' })}
          />
          {renderError('ragioneSociale', errors.ragioneSociale?.message)}
        </div>

        <div>
          <label htmlFor="categoria">Categoria*</label>
          <select
            id="categoria"
            {...register('categoria', { required: 'Campo obbligatorio' })}
          >
            <option value="">Seleziona una categoria</option>
            {categorie.map((cat) => (
              <option key={cat.code} value={cat.code}>
                {cat.descrizione}
              </option>
            ))}
          </select>
          {renderError('categoria', errors.categoria?.message)}
        </div>

        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Creazione...' : 'Crea Utente'}
        </button>
      </form>
      <ToastContainer />
    </div>
  );
};

export default CreaUtente;
